import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["content"],
  _excluded2 = ["extraDisplayContentPaths", "extraDisplayIndexes", "displayFields"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getValue } from '@truqc/content-path';
import { keyPathsToCriteria } from '../../behaviors/src/lib/formatPaths';
const CABINET_COLUMN_TAG = 'cabinetColumn';

/**
 * Converts a form contentPath to the row key used in list data and SQL aliases.
 */
export const pathToColumnKey = path => {
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  return normalized.replace(/\//g, '_');
};

/**
 * Extracts the display title from an option picker value (object or JSON string).
 */
export const formatOptionPickerDisplayValue = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  let parsed = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return value;
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch (_unused) {
      return value;
    }
  }
  if (typeof parsed === 'string' || typeof parsed === 'number') {
    return String(parsed);
  }
  if (Array.isArray(parsed)) {
    return parsed.map(item => formatOptionPickerDisplayValue(item)).filter(Boolean).join(', ');
  }
  if (typeof parsed !== 'object') {
    return String(parsed);
  }
  if (typeof parsed.title === 'string') {
    return parsed.title;
  }
  const nested = Object.values(parsed);
  for (let i = 0; i < nested.length; i += 1) {
    const current = nested[i];
    if (current && typeof current === 'object' && typeof current.title === 'string') {
      return current.title;
    }
    if (typeof current === 'string') {
      return current;
    }
  }
  return '';
};

/**
 * Formats a cabinet list cell value for display (option pickers, plain text, numbers).
 */
export const formatCabinetCellValue = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'string' && (value.trim().startsWith('{') || value.trim().startsWith('['))) {
    const formatted = formatOptionPickerDisplayValue(value);
    return formatted || value;
  }
  if (typeof value === 'object') {
    return formatOptionPickerDisplayValue(value) || '';
  }
  return value;
};

/**
 * Converts a camelCase key to a human-readable title.
 */
export const humanizeKey = key => key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();

/**
 * Scans formatInput for fields tagged with 'cabinetColumn'.
 *
 * Uses keyPathsToCriteria, which only walks `children`. Tags on fields inside
 * unresolved template references (a node with `type` but no inlined `children`)
 * are not discovered. Provisioned FormType.formatInput is expanded at migrate
 * time, so tags in template source files appear after content deploy.
 */
export const extractTaggedFieldsFromFormatInput = formatInput => {
  if (!formatInput) return [];
  let parsed = formatInput;
  if (typeof formatInput === 'string') {
    try {
      parsed = JSON.parse(formatInput);
    } catch (_unused2) {
      return [];
    }
  }
  let matches = [];
  try {
    matches = keyPathsToCriteria({
      tags: [CABINET_COLUMN_TAG]
    }, parsed);
  } catch (_unused3) {
    return [];
  }
  return matches.filter(_ref => {
    let component = _ref.component;
    return typeof (component === null || component === void 0 ? void 0 : component.label) === 'string' && component.label.length > 0;
  }).map(_ref2 => {
    let path = _ref2.path,
      component = _ref2.component;
    const contentPath = path.startsWith('/') ? path : "/".concat(path);
    return {
      key: pathToColumnKey(contentPath),
      title: component.label,
      contentPath
    };
  });
};

/**
 * Collects all cabinetColumn-tagged fields across the cabinet's form types.
 */
export const getCabinetCandidateFields = (formTypeIdentifiers, formTypesByIdentifier) => {
  const seen = new Set();
  const fields = [];
  formTypeIdentifiers.forEach(identifier => {
    const formType = formTypesByIdentifier[identifier];
    if (!(formType !== null && formType !== void 0 && formType.formatInput)) return;
    extractTaggedFieldsFromFormatInput(formType.formatInput).forEach(field => {
      if (!seen.has(field.key)) {
        seen.add(field.key);
        fields.push(field);
      }
    });
  });
  return fields;
};

/**
 * Returns the set of index keys already rendered by active columns.
 */
const getActiveIndexSet = activeColumns => new Set(activeColumns.flatMap(col => col.rows.flatMap(row => {
  if (row.index) return [row.index];
  if (Array.isArray(row.indexes)) return row.indexes;
  return [];
})));

/**
 * Filters tagged fields to those not already shown in static or added columns.
 */
export const getAvailableFields = function getAvailableFields(candidateFields, activeColumns) {
  let addedColumnIndexes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  const activeIndexes = getActiveIndexSet(activeColumns);
  const addedSet = new Set(addedColumnIndexes);
  return candidateFields.filter(_ref3 => {
    let key = _ref3.key;
    return !activeIndexes.has(key) && !addedSet.has(key);
  });
};

/**
 * Produces a minimal column definition for a user-added key.
 * Deliberately omits sortIndex so the column is presentation-only.
 */
export const toColumnDef = function toColumnDef(key) {
  let candidateFields = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const found = candidateFields.find(f => f.key === key);
  return {
    title: found ? found.title : humanizeKey(key),
    rows: [{
      index: key
    }]
  };
};
const parseRowContent = content => {
  if (content === null || content === undefined) {
    return null;
  }
  if (typeof content === 'object') {
    return content;
  }
  if (typeof content === 'string') {
    try {
      return JSON.parse(content);
    } catch (_unused4) {
      return null;
    }
  }
  return null;
};

/**
 * Reads customized column values from each row's form content (client-side).
 *
 * @param {boolean} [omitContentAfterEnrich=true] Drop `content` from enriched rows
 *   so the grid does not retain full form JSON in React state after extraction.
 */
export const enrichCabinetListRows = function enrichCabinetListRows(list, addedColumnKeys) {
  let candidateFields = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  let omitContentAfterEnrich = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  if (!addedColumnKeys.length || !(list !== null && list !== void 0 && list.length)) {
    return list;
  }
  const pathsByKey = addedColumnKeys.map(key => {
    const field = candidateFields.find(f => f.key === key);
    return field ? {
      key,
      contentPath: field.contentPath
    } : null;
  }).filter(Boolean);
  if (!pathsByKey.length) {
    return list;
  }
  return list.map(row => {
    const content = parseRowContent(row.content);
    if (!content) {
      return row;
    }
    const extracted = {};
    pathsByKey.forEach(_ref4 => {
      let key = _ref4.key,
        contentPath = _ref4.contentPath;
      extracted[key] = getValue(contentPath, content);
    });
    const enriched = _objectSpread(_objectSpread({}, row), extracted);
    if (!omitContentAfterEnrich || !Object.prototype.hasOwnProperty.call(enriched, 'content')) {
      return enriched;
    }
    const _content = enriched.content,
      withoutContent = _objectWithoutProperties(enriched, _excluded);
    return withoutContent;
  });
};

/**
 * Request full form content on the list query (existing findAll displayFields support).
 */
export const withCabinetListContent = (query, includeContent) => {
  const _ref5 = query || {},
    _legacyPaths = _ref5.extraDisplayContentPaths,
    _legacyIndexes = _ref5.extraDisplayIndexes,
    _displayFields = _ref5.displayFields,
    rest = _objectWithoutProperties(_ref5, _excluded2);
  if (!includeContent) {
    return rest;
  }
  return _objectSpread(_objectSpread({}, rest), {}, {
    displayFields: ['content']
  });
};

/**
 * Re-map legacy preference keys to current content-path row keys.
 */
export const normalizeAddedColumnKeys = function normalizeAddedColumnKeys(savedKeys) {
  let candidateFields = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!savedKeys.length || !candidateFields.length) {
    return savedKeys;
  }
  const normalized = savedKeys.map(key => {
    const direct = candidateFields.find(f => f.key === key);
    if (direct) return direct.key;
    const legacy = candidateFields.find(f => pathToColumnKey(f.contentPath) === key || f.contentPath === key);
    return (legacy === null || legacy === void 0 ? void 0 : legacy.key) || key;
  });
  const seen = new Set();
  return normalized.filter(key => {
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};